/**
 * TinyMCE Configuration Generator
 *
 * Generates TinyMCE configuration based on field attributes.
 * Handles both restricted mode (with allowed HTML tags) and default mode.
 * This utility is used when integrations need to restrict HTML tags
 * (e.g., Telegram, WhatsApp, etc.)
 *
 * @since 5.10
 */
/**
 * Internal class for TinyMCE configuration generation
 */
class TinyMCEConfigGenerator {
    /**
     * Constructor
     *
     * @param attributes Field attributes object
     * @param tinyMCEStyles Array of CSS URLs for TinyMCE
     */
    constructor(attributes, tinyMCEStyles) {
        this.attributes = attributes;
        this.tinyMCEStyles = tinyMCEStyles;
    }
    /**
     * Generate TinyMCE configuration
     * Automatically determines whether to use restricted or default config
     *
     * @return Complete TinyMCE options object
     */
    generate() {
        return this.attributes.allowedHtmlTags
            ? this.buildRestrictedConfig()
            : this.buildDefaultConfig();
    }
    /**
     * Build complete restricted TinyMCE configuration
     *
     * @return Complete TinyMCE options object
     */
    buildRestrictedConfig() {
        // Generate tag-specific configuration
        const tagConfig = this.generateTagConfig(this.attributes.allowedHtmlTags || []);
        const restrictedModeOptions = this.getRestrictedModeOptions();
        return {
            mediaButtons: false, // Always disable media for restricted mode
            tinymce: {
                plugins: tagConfig.plugins.join(' '),
                toolbar1: tagConfig.toolbar.join(' '),
                height: '350',
                content_css: this.tinyMCEStyles.join(','),
                valid_elements: tagConfig.validElements || undefined,
                formats: tagConfig.formats,
                ...restrictedModeOptions,
            },
            quicktags: true,
        };
    }
    /**
     * Build default TinyMCE configuration (standard, no restrictions)
     *
     * @return Complete TinyMCE options object
     */
    buildDefaultConfig() {
        // TinyMCE plugins
        const tinymcePlugins = ['charmap', 'colorpicker', 'hr', 'lists', 'paste', 'tabfocus', 'textcolor', 'fullscreen', 'link'];
        // Check if it supports fullpage editing
        if (this.attributes.supportsFullPageEditing) {
            tinymcePlugins.push('fullpage');
        }
        // TinyMCE toolbar
        const tinymceToolbar = ['formatselect', 'bold', 'italic', 'bullist', 'numlist', 'blockquote', 'alignleft', 'aligncenter', 'alignright', 'link', 'unlink', 'wp_more', 'strikethrough', 'hr', 'forecolor', 'pastetext', 'removeformat', 'visualchars', 'outdent', 'indent', 'undo', 'redo'];
        // TinyMCE config object
        return {
            mediaButtons: this.attributes.supportsMedia !== false,
            tinymce: {
                plugins: tinymcePlugins.join(' '),
                toolbar1: tinymceToolbar.join(' '),
                height: '350',
                content_css: this.tinyMCEStyles.join(','),
            },
            quicktags: true,
        };
    }
    /**
     * Get additional TinyMCE options for restricted HTML mode
     *
     * @return Additional TinyMCE options
     */
    getRestrictedModeOptions() {
        return {
            // Configure to use <br> for line breaks instead of <p> tags
            forced_root_block: '',
            force_br_newlines: true,
            force_p_newlines: false,
            keep_styles: false,
            // Allow valid_children configuration
            valid_children: '+body[style]',
        };
    }
    /**
     * Generate TinyMCE configuration from allowed HTML tags
     * Private method that maps tags to TinyMCE config
     *
     * @param allowedTags Array of allowed HTML tag names (e.g., ['b', 'i', 'a', 'br'])
     * @return TinyMCE configuration object with plugins, toolbar, formats, and validElements
     */
    generateTagConfig(allowedTags) {
        const config = {
            plugins: ['paste'],
            toolbar: [],
            formats: {},
            validElements: null,
        };
        if (!allowedTags || !Array.isArray(allowedTags) || allowedTags.length === 0) {
            return config;
        }
        // Map HTML tags to TinyMCE toolbar buttons and plugins
        if (allowedTags.includes('b') || allowedTags.includes('strong')) {
            config.toolbar.push('bold');
            config.formats.bold = { inline: 'b' };
        }
        if (allowedTags.includes('i') || allowedTags.includes('em')) {
            config.toolbar.push('italic');
            config.formats.italic = { inline: 'i' };
        }
        if (allowedTags.includes('u') || allowedTags.includes('ins')) {
            config.toolbar.push('underline');
            config.formats.underline = { inline: 'u' };
        }
        if (allowedTags.includes('s') || allowedTags.includes('strike') || allowedTags.includes('del')) {
            config.toolbar.push('strikethrough');
            config.formats.strikethrough = { inline: 's' };
        }
        if (allowedTags.includes('a')) {
            config.toolbar.push('link', 'unlink');
            config.plugins.push('link');
        }
        if (allowedTags.includes('blockquote')) {
            config.toolbar.push('blockquote');
        }
        if (allowedTags.includes('ul') || allowedTags.includes('ol')) {
            config.toolbar.push('bullist', 'numlist');
            config.plugins.push('lists');
        }
        // Add standard editing controls
        config.toolbar.push('removeformat', 'undo', 'redo');
        // Build valid elements string
        config.validElements = this.buildValidElements(allowedTags);
        return config;
    }
    /**
     * Build valid_elements string for TinyMCE
     * Private method that ensures token preservation
     *
     * This ensures that:
     * 1. All allowed tags from the integration are included
     * 2. Automator token wrapper elements are always preserved (ins, span, img)
     * 3. Required attributes are specified for each element
     *
     * @param allowedTags Array of allowed HTML tag names
     * @return TinyMCE valid_elements string
     */
    buildValidElements(allowedTags) {
        const validElements = [...allowedTags]; // Clone array
        // IMPORTANT: Always allow ins, span, and img for Automator tokens
        // Tokens are rendered as:
        // <ins><span class="uap-token">
        //   <span class="uap-token__icon"><img src="..."></span>
        //   <span class="uap-token__name">Token Name</span>
        // </span></ins>
        if (!validElements.includes('ins')) {
            validElements.push('ins');
        }
        if (!validElements.includes('span')) {
            validElements.push('span');
        }
        if (!validElements.includes('img')) {
            validElements.push('img');
        }
        let validElementsString = validElements.join(',');
        // Add attributes for specific elements
        validElementsString = this.addElementAttributes(validElementsString);
        return validElementsString;
    }
    /**
     * Add required attributes to valid_elements string
     * Private method for attribute mapping
     *
     * @param validElementsString Initial valid_elements string
     * @return Updated valid_elements string with attributes
     */
    addElementAttributes(validElementsString) {
        // Link attributes
        if (validElementsString.includes('a')) {
            validElementsString = validElementsString.replace('a', 'a[href]');
        }
        // Automator token wrapper attributes
        validElementsString = validElementsString.replace('ins', 'ins[contenteditable]');
        validElementsString = validElementsString.replace('span', 'span[class|contenteditable|data-token-id]');
        // Token icon attributes
        validElementsString = validElementsString.replace('img', 'img[src|alt|class]');
        return validElementsString;
    }
}
/**
 * Generate TinyMCE configuration
 *
 * Callable utility function that generates TinyMCE configuration based on field attributes.
 * Automatically determines whether to use restricted or default configuration.
 *
 * @param attributes Field attributes object
 * @param tinyMCEStyles Array of CSS URLs for TinyMCE
 * @return Complete TinyMCE options object
 *
 * @example
 * const tinyOptions = generateTinyMCEConfig( this.attributes, tinyMCEStyles );
 * wp.editor.initialize( fieldId, tinyOptions );
 */
const generateTinyMCEConfig = (attributes, tinyMCEStyles) => {
    const generator = new TinyMCEConfigGenerator(attributes, tinyMCEStyles);
    return generator.generate();
};
export default generateTinyMCEConfig;
